import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { Check } from "lucide-react";
import { CheckboxBase, type CheckboxBaseProps } from "@core/ui";

export type Props = Omit<CheckboxBaseProps, "className" | "aria-label"> & {
  /** Focus handler */
  onFocus?: () => void;
  /** Accessible label for screen readers */
  ariaLabel?: string;
  /** Additional CSS classes */
  className?: string;
};

/**
 * Styled checkbox component wrapping CheckboxBase with custom checkmark.
 * Pure UI component - works with or without react-hook-form.
 *
 * @example
 * // Standalone usage
 * <Checkbox
 *   name="agree"
 *   checked={agreed}
 *   onChange={setAgreed}
 *   ariaLabel="I agree to the terms"
 * />
 */
export const Checkbox = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      id,
      checked = false,
      onChange,
      onBlur,
      onFocus,
      error,
      disabled,
      ariaLabel,
      className,
      ...props
    },
    ref
  ) => {
    const inputId = id ?? name;

    return (
      <label
        htmlFor={inputId}
        className={twMerge(
          "inline-flex items-center cursor-pointer",
          disabled && "cursor-not-allowed",
          className
        )}
      >
        <CheckboxBase
          ref={ref}
          id={inputId}
          name={name}
          checked={checked}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          error={error}
          aria-label={ariaLabel}
          className="peer absolute opacity-0 w-5 h-5"
          {...props}
        />
        <div
          className={twMerge(
            "h-5 w-5 shrink-0 rounded-[4px] border shadow text-transparent",
            "border-brand-800",
            checked && "bg-brand-800 text-brand-50 border-0",
            disabled && "cursor-not-allowed bg-brand-200 border-brand-300",
            disabled && checked && "bg-brand-200 border-brand-300 text-brand-400",
            error && "border-error-600"
          )}
        >
          <div className="flex items-center justify-center">
            <Check className="h-5 w-5" />
          </div>
        </div>
      </label>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;

